import { Component, ElementRef, ViewChild, ViewEncapsulation, type OnDestroy, type OnInit } from '@angular/core';
import {
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  Validators,
  type UntypedFormGroup,
} from '@angular/forms';

import { Store } from '@ngrx/store';
import { Subject, Subscription, type Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, take } from 'rxjs/operators';

import { LoaderType } from '@fcom/common/';
import { GtmService } from '@fcom/common/gtm';
import { type SelectOption } from '@fcom/common/interfaces';
import { PhonePrefixService, ScrollService } from '@fcom/common/services';
import { type Location } from '@fcom/core-api';
import { type LoginAddress, type Profile } from '@fcom/core-api/login';
import { type AppState } from '@fcom/core/interfaces';
import { profileOrUndefinedIfNotLoggedIn } from '@fcom/core/selectors';
import {
  EMAIL_REGEX,
  LocalDate,
  NAME_PATTERN,
  PHONE_PATTERN,
  unsubscribe,
  WORLDTRACER_PATTERN,
} from '@fcom/core/utils';
import {
  dateValidator,
  disableConditionalFields,
  enableConditionalFields,
  FormStatus,
  frequentFlyerValidator,
  trackFormState,
  type CheckboxOptionList,
  type TypedFormGroup,
} from '@fcom/service-forms';
import { DatepickerTheme } from '@fcom/service-forms/components/common/datepicker.component';
import {
  ButtonType,
  NotificationLayout,
  NotificationTheme,
  RadioItemGroupOption,
  TextInputType,
} from '@fcom/ui-components';
import { LanguageService } from '@fcom/ui-translate';

import { BaggageTracingService } from '../services/baggage-tracing.service';
import { buildAddress, FLIGHT_NUMBER_PATTERN, mapProfileTier, parseCountryCode } from '../utils';

@Component({
  selector: 'fin-baggage-tracing-form',
  templateUrl: 'baggage-tracing-form.component.html',
  styleUrls: ['./special-assistance-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class BaggageTracingFormComponent implements OnInit, OnDestroy {
  public readonly LoaderType = LoaderType;
  public readonly FormStatus = FormStatus;
  public readonly TextInputType = TextInputType;
  public readonly ButtonType = ButtonType;

  readonly NotificationLayout = NotificationLayout;
  readonly NotificationTheme = NotificationTheme;

  @ViewChild('formContainer', { static: true })
  formContainer: ElementRef;

  destinationLoading$: Subject<boolean> = new Subject();
  departureLoading$: Subject<boolean> = new Subject();
  destinationLocations$: Subject<Location[]> = new Subject();
  departureLocations$: Subject<Location[]> = new Subject();
  minDate: LocalDate;
  maxDate: LocalDate;
  toMinDate: LocalDate;
  reactiveForm: TypedFormGroup;
  formStatus: FormStatus = FormStatus.INITIAL;
  completedServiceRequestNumber = '"ERROR, not found"';
  itemOwnerOptions$: Observable<SelectOption[]>;
  bagOptionsControls: CheckboxOptionList;
  phonePrefixes$: Observable<SelectOption[]>;
  finnairPlusTierOptions: RadioItemGroupOption[] = [
    { value: 'platinumLumo' },
    { value: 'platinum' },
    { value: 'gold' },
    { value: 'silver' },
    { value: 'basic' },
    { value: 'none' },
  ];

  bagItemsFormGroupConfig: { [key: string]: any } = {
    itemName: [''],
    itemDescription: [''],
    itemBelongsTo: [''],
  };

  iteneraryFormGroupConfig: { [key: string]: any } = {
    origin: ['', [Validators.required]],
    destination: ['', [Validators.required]],
    flightNumber: ['', [Validators.required, Validators.pattern(FLIGHT_NUMBER_PATTERN)]],
    flightDate: ['', [Validators.required, dateValidator()]],
  };

  private subscriptions: Subscription = new Subscription();

  constructor(
    private fb: UntypedFormBuilder,
    private scrollService: ScrollService,
    private gtmService: GtmService,
    private languageService: LanguageService,
    private phonePrefixService: PhonePrefixService,
    private baggageTracingService: BaggageTracingService,
    private store$: Store<AppState>
  ) {}

  ngOnInit(): void {
    this.phonePrefixes$ = this.phonePrefixService.setupPhonePrefixes();
    this.bagOptionsControls = this.baggageTracingService.bagOptionsControls;

    this.subscriptions.add(
      this.store$.pipe(profileOrUndefinedIfNotLoggedIn(), take(1)).subscribe((profile) => {
        this.createForm(profile);
        this.addFormWatchers();
      })
    );

    this.translateItemOwnerOptions();
    this.destinationLoading$.next(false);
    this.departureLoading$.next(false);

    this.minDate = LocalDate.now();
    this.maxDate = LocalDate.forDate(this.minDate.localYear + 2, 1, 1);
    this.toMinDate = LocalDate.now();
  }

  ngOnDestroy(): void {
    this.subscriptions = unsubscribe(this.subscriptions);
  }

  createForm(profile: Profile): void {
    // Create a new array with a form control for each order
    const controls: UntypedFormControl[] = this.bagOptionsControls.map(() => new UntypedFormControl(false));
    const primaryAddress: LoginAddress = profile?.addresses?.find((address) => address.primary);

    this.reactiveForm = this.fb.group({
      baggageReferenceNumber: ['', Validators.pattern(WORLDTRACER_PATTERN)],
      firstName: [
        profile?.firstname ?? '',
        [Validators.required, Validators.pattern(NAME_PATTERN), Validators.maxLength(50)],
      ],
      lastName: [
        profile?.lastname ?? '',
        [Validators.required, Validators.pattern(NAME_PATTERN), Validators.maxLength(50)],
      ],
      phonePrefix: [
        parseCountryCode(profile?.parsedMobilePhone?.regionCodes, profile?.parsedMobilePhone?.countryCode),
        [Validators.required],
      ],
      phoneNumber: [
        profile?.parsedMobilePhone?.nationalNumber ?? '',
        [Validators.required, Validators.pattern(PHONE_PATTERN)],
      ],
      email: [profile?.email ?? '', [Validators.required, Validators.pattern(EMAIL_REGEX)]],
      homeAddress: [buildAddress(primaryAddress), [Validators.required]],
      temporaryAddress: [''],
      temporaryAddressValidFrom: [{ value: '', disabled: true }, [Validators.required, dateValidator()]],
      temporaryAddressValidTo: [{ value: '', disabled: true }, [Validators.required, dateValidator()]],
      cardNumber: [profile?.memberNumber ?? '', [frequentFlyerValidator()]],
      finnairPlusTier: [mapProfileTier(profile?.tier)],
      itenerary: this.fb.array([]),
      baggageLastSeen: ['', [Validators.required]],
      numberOfPiecesChecked: ['', [Validators.required, Validators.pattern(/^\d+$/)]],
      baggageIdentificationTagNumbers: [''],
      whenAndWhereLossReported: [''],
      anythingParticularAtCheckIn: [''],
      color: ['', [Validators.required]],
      brand: [''],
      typeOfBag: ['', [Validators.required]],
      bagWasHard: ['', [Validators.required]],
      bagOptions: new UntypedFormArray(controls),
      combinationLockCode: [{ value: '', disabled: true }],
      baggageNameTagDescription: [''],
      baggageIdentifiableFeatures: [''],
      bagItems: this.fb.array([]),
      itemsToIdentifyBaggage: [''],
    }) as TypedFormGroup;
  }

  onSubmit(): void {
    if (!this.reactiveForm.valid) {
      this.reactiveForm.markAllAsTouched();
      this.reactiveForm.updateValueAndValidity();

      return;
    }

    this.formStatus = FormStatus.LOADING;
    this.scrollService.scrollToImmediate(this.formContainer);

    this.subscriptions.add(
      this.baggageTracingService
        .baggageTracing(this.reactiveForm.value)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.formStatus = FormStatus.COMPLETE;
            trackFormState('baggage-tracing', 'success', this.gtmService);
          },
          error: () => {
            this.formStatus = FormStatus.ERROR;
            trackFormState('baggage-tracing', 'error', this.gtmService);
          },
        })
    );
  }

  private addFormWatchers(): void {
    this.watchFieldChanges(this.reactiveForm, this.updateConditionalFields.bind(this), 'bagOptions');

    this.watchFieldChanges(this.reactiveForm, this.updateTemporaryAddressFields.bind(this), 'temporaryAddress');

    this.watchFieldChanges(
      this.reactiveForm,
      this.updateTemporaryAddressToMinDate.bind(this),
      'temporaryAddressValidFrom'
    );

    this.subscriptions.add(
      this.reactiveForm.statusChanges
        .pipe(
          filter((status) => status === 'INVALID'),
          distinctUntilChanged(),
          take(1)
        )
        .subscribe(() => {
          trackFormState('request-special-assistance', 'start', this.gtmService);
        })
    );
  }

  private updateTemporaryAddressToMinDate(inputValue: LocalDate): void {
    if (inputValue) {
      this.toMinDate = inputValue;
    }
  }

  private updateConditionalFields(inputValue: boolean[]): void {
    const selected = this.baggageTracingService.parseFormArrayOptions(inputValue, this.bagOptionsControls);
    if (selected['Combination lock']) {
      enableConditionalFields(['combinationLockCode'], this.reactiveForm);
    } else {
      disableConditionalFields(['combinationLockCode'], this.reactiveForm);
    }
  }

  private updateTemporaryAddressFields(inputValue: string): void {
    if (inputValue.length > 0) {
      enableConditionalFields(['temporaryAddressValidFrom', 'temporaryAddressValidTo'], this.reactiveForm);
    } else {
      disableConditionalFields(['temporaryAddressValidFrom', 'temporaryAddressValidTo'], this.reactiveForm);
    }
  }

  private watchFieldChanges(
    form: UntypedFormGroup,
    callback: (value: boolean[] | LocalDate | string) => void,
    field: string
  ): void {
    this.subscriptions.add(
      form.get(field).valueChanges.subscribe((val) => {
        callback(val);
      })
    );
  }

  private translateItemOwnerOptions(): void {
    this.itemOwnerOptions$ = this.languageService
      .translate('SERVICE_FORMS.NTServiceForms.fields.itemBelongsTo.options')
      .pipe(
        map((options: Record<string, string>) =>
          Object.keys(options).map(
            (value) =>
              ({
                value: value,
                name: options[value],
              }) as SelectOption
          )
        )
      );
  }

  get datepickerThemes(): typeof DatepickerTheme {
    return DatepickerTheme;
  }
}
